import { inject, untracked } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { Response } from '@shared/models/response.model';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { LivrableUpdateRequest } from '../request/update-request';
import { RéférentielLivrableService } from '../référentiel-livrable.service';
import { RéférentielLivrableState } from './référentiel-livrable.state';
import {
  RéférentielLivrable,
  ÉtatRéférentielLivrable,
} from '../../administration/gestion-référentiels/models/référentiel-livrable.model';

export function avecMéthodesRéférentielLivrables<_>() {
  return signalStoreFeature(
    {
      state: type<RéférentielLivrableState & EntityState<RéférentielLivrable>>(),
      props: type<EntityProps<RéférentielLivrable>>(),
    },
    withMethods((store) => ({
      chargerLivrables: (state: { livrables: RéférentielLivrable[] }) =>
        patchState(store, setAllEntities(state.livrables)),
      modifierLivrable: (state: { id: string; livrable: Partial<RéférentielLivrable> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.livrable })),
    })),
    withMethods((store, service = inject(RéférentielLivrableService)) => ({
      charger: rxMethod<void>(
        pipe(
          switchMap(() =>
            service.findAll().pipe(
              tapResponse({
                next: (result: Response<RéférentielLivrable>) => {
                  patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                  store.chargerLivrables({ livrables: result.items });
                },
                error: () => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Un problème est survenu lors du chargement des référentiels, contactez le support.',
                      titre: "Le chargement des cartographies n'a pas abouti",
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{ id: string; data: LivrableUpdateRequest }>(
        pipe(
          switchMap(({ id, data }) =>
            service.update(id, data).pipe(
              tapResponse({
                next: () => store.modifierLivrable({ id, livrable: data }),
                error: () => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Un problème est survenu lors du chargement des référentiels, contactez le support.',
                      titre: "Le chargement des cartographies n'a pas abouti",
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
    withMethods((store) => ({
      // Filtres
      setFiltres: (filtres: {
        cartographies?: string[];
        institutsMetiers?: string[];
        services?: string[];
        livrables?: LivrablePrincipal[];
        statuts?: ÉtatRéférentielLivrable[];
        echeance?: 'ONE_WEEK' | 'ONE_MONTH' | 'THREE_MONTHS' | undefined;
      }) => {
        patchState(store, { filtres, paginator: { ...untracked(store.paginator), pageNb: 1 } });
        sessionStorage.setItem('filtres', JSON.stringify(filtres));
      },
      setLoading: (loading: boolean) => patchState(store, { loading }),
    })),
  );
}
